/**
 * useVirtualKeyboard - Adjusts layout when the virtual keyboard on touch devices appears.
 *
 * Mobile Safari shrinks `window.visualViewport` when the keyboard opens,
 * but the layout viewport stays the same size — causing the input area to
 * be hidden behind the keyboard. This hook sets a CSS custom property
 * `--keyboard-inset` on the document element so that CSS can offset the
 * bottom of the input area.
 *
 * Usage: Call once at the app level. Then use `var(--keyboard-inset)` in CSS
 * for bottom padding/margin on the input container.
 */

import { useEffect } from 'react'

const KEYBOARD_GAP_MIN_PX = 120
const KEYBOARD_GAP_MIN_RATIO = 0.16

function isEditableElement(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true

  if (target instanceof HTMLTextAreaElement) return true
  if (!(target instanceof HTMLInputElement)) return false

  const nonTextInputTypes = new Set([
    'button',
    'checkbox',
    'color',
    'file',
    'hidden',
    'image',
    'radio',
    'range',
    'reset',
    'submit',
  ])

  return !nonTextInputTypes.has(target.type)
}

export function useVirtualKeyboard(): void {
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    let rafId = 0

    // In standalone PWA mode, visualViewport excludes safe areas but
    // innerHeight includes them — so the naive difference picks up the
    // bottom safe area as a false "keyboard inset". Capture the baseline
    // gap on mount (before any keyboard opens) and subtract it.
    let baselineGap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)

    const setInset = (value: number) => {
      const inset = Math.max(0, Math.round(value))
      document.documentElement.style.setProperty('--keyboard-inset', `${inset}px`)
    }

    const update = () => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        const rawGap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
        const editableFocused = isEditableElement(document.activeElement)
        const keyboardGapThreshold = Math.max(
          KEYBOARD_GAP_MIN_PX,
          Math.round(window.innerHeight * KEYBOARD_GAP_MIN_RATIO)
        )
        const keyboardLikelyOpen = editableFocused && rawGap >= keyboardGapThreshold

        // Treat small visual viewport gaps as browser chrome/safe-area movement,
        // not keyboard. We only shift layout when the keyboard is likely open.
        if (!keyboardLikelyOpen) {
          baselineGap = rawGap
          setInset(0)
          return
        }

        const inset = Math.max(0, rawGap - baselineGap)
        setInset(inset)
      })
    }

    const handleFocusChange = () => update()

    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    window.addEventListener('focusin', handleFocusChange)
    window.addEventListener('focusout', handleFocusChange)
    window.addEventListener('orientationchange', handleFocusChange)
    update()

    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
      window.removeEventListener('focusin', handleFocusChange)
      window.removeEventListener('focusout', handleFocusChange)
      window.removeEventListener('orientationchange', handleFocusChange)
      cancelAnimationFrame(rafId)
      document.documentElement.style.removeProperty('--keyboard-inset')
    }
  }, [])
}
